import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import {
  FieldHelp,
  SENSITIVE_FIELD_HELP,
  type FieldHelpDisplay,
} from "./FieldHelp";

/** Conceptual width tokens — full CSS grid wiring lands in Wave 3.1 / 4.2. */
export type FormFieldWidth = "sm" | "date" | "md" | "lg";

const WIDTH_CLASS: Record<FormFieldWidth, string> = {
  sm: "max-w-[8rem]",
  date: "max-w-[11.5rem]",
  md: "",
  lg: "max-w-2xl",
};

/**
 * The one label / help / error frame every control sits in. `TF` and
 * `FieldWrap` delegate here so the three tiers of copy (label, hint,
 * error) are styled in exactly one place.
 *
 * - Primary label is always visible.
 * - `hint` defaults to an info-icon tooltip (operator forms); pass
 *   `helpDisplay="inline"` for merchant-facing copy that must be seen.
 * - `sensitive` adds a lock-icon tooltip explaining last-4 retention.
 * - `error` always renders inline, replacing an inline hint.
 */
export function FormField({
  id,
  label,
  required,
  error,
  hint,
  helpDisplay = "tooltip",
  sensitive,
  sensitiveHelpText = SENSITIVE_FIELD_HELP,
  width = "md",
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  helpDisplay?: FieldHelpDisplay;
  sensitive?: boolean;
  sensitiveHelpText?: string;
  width?: FormFieldWidth;
  children: ReactNode;
}) {
  const errId = `${id}-err`;
  const hintInlineId =
    hint && helpDisplay === "inline" && !error ? `${id}-hint` : undefined;

  return (
    <div className={`min-w-0 ${WIDTH_CLASS[width]}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-1.5">
        <label htmlFor={id} className="label inline">
          {label}
          {required && (
            <span className="required-marker" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {hint && helpDisplay === "tooltip" && (
          <FieldHelp
            id={`${id}-hint`}
            text={hint}
            display="tooltip"
            variant="info"
          />
        )}
        {sensitive && (
          <FieldHelp
            id={`${id}-sensitive`}
            text={sensitiveHelpText}
            display="tooltip"
            variant="lock"
          />
        )}
      </div>
      {children}
      {hintInlineId && (
        <FieldHelp id={hintInlineId} text={hint!} display="inline" />
      )}
      {error && <FieldErrorText id={errId}>{error}</FieldErrorText>}
    </div>
  );
}

/** Inline validation message: red, medium weight, with an icon so it can
 *  never be mistaken for a hint. Shared by FormField and the few places
 *  that render a group-level error (percent totals, etc.). */
export function FieldErrorText({
  id,
  children,
  className = "",
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p id={id} role="alert" className={`field-error ${className}`}>
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** Build aria-describedby for a control inside FormField. */
export function formFieldDescribedBy(
  id: string,
  opts: {
    error?: string;
    hint?: string;
    helpDisplay?: FieldHelpDisplay;
    extra?: string;
  },
): string | undefined {
  const ids = [
    opts.extra,
    opts.error ? `${id}-err` : undefined,
    opts.hint && opts.helpDisplay === "inline" ? `${id}-hint` : undefined,
  ].filter(Boolean);
  return ids.length > 0 ? ids.join(" ") : undefined;
}
